import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  ArrowLeft,
  User,
  CreditCard,
  Calendar,
  Shield,
  Clock,
  AlertTriangle,
  RefreshCw,
  Pause,
  Play,
  Gift,
  CalendarPlus,
  ChevronDown,
  Loader2,
  Mail,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800 border-green-200',
  trialing: 'bg-blue-100 text-blue-800 border-blue-200',
  past_due: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  canceled: 'bg-gray-100 text-gray-600 border-gray-200',
  suspended: 'bg-red-100 text-red-800 border-red-200',
  expired: 'bg-gray-100 text-gray-500 border-gray-200',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
    {status?.replace(/_/g, ' ')}
  </span>
);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

// Confirmation modal for dangerous actions
function ConfirmModal({ open, onClose, title, description, confirmLabel, variant, loading, onConfirm, children }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {variant === 'destructive' && <AlertTriangle className="h-5 w-5 text-red-500" />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter className="gap-2 sm:gap-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50 ${
              variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel || 'Confirm'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSubscriberDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [forceStatusModal, setForceStatusModal] = useState(false);
  const [extendModal, setExtendModal] = useState(false);
  const [grantPlanModal, setGrantPlanModal] = useState(false);

  // Form states
  const [forceStatus, setForceStatus] = useState('active');
  const [reason, setReason] = useState('');
  const [extendDays, setExtendDays] = useState(30);
  const [grantPlan, setGrantPlan] = useState('pro');
  const [grantDays, setGrantDays] = useState(30);

  const [plans, setPlans] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [subData, planData] = await Promise.all([
        adminSubscriptionApi.getSubscriber(userId),
        adminSubscriptionApi.listPlans(),
      ]);
      setData(subData);
      setPlans(planData.data || planData.plans || planData || []);
    } catch (err) {
      toast.error('Failed to load subscriber details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleForceStatus = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await adminSubscriptionApi.forceStatus(data.subscription?.id, forceStatus, reason);
      toast.success(`Status changed to ${forceStatus}`);
      setForceStatusModal(false);
      setReason('');
      fetchData();
    } catch (err) {
      toast.error('Failed to change status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await adminSubscriptionApi.extendSubscription(data.subscription?.id, extendDays, reason);
      toast.success(`Subscription extended by ${extendDays} days`);
      setExtendModal(false);
      setReason('');
      fetchData();
    } catch (err) {
      toast.error('Failed to extend subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrantPlan = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await adminSubscriptionApi.grantPlan(userId, grantPlan, reason, grantDays);
      toast.success(`Plan ${grantPlan} granted for ${grantDays} days`);
      setGrantPlanModal(false);
      setReason('');
      fetchData();
    } catch (err) {
      toast.error('Failed to grant plan');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-md hover:bg-accent transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="animate-pulse">
            <div className="h-7 w-48 bg-muted rounded" />
            <div className="h-4 w-32 bg-muted rounded mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-5 w-32 bg-muted rounded" />
                  <div className="h-4 w-full bg-muted rounded" />
                  <div className="h-4 w-3/4 bg-muted rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Subscriber not found</p>
          <button
            onClick={() => navigate('/admin/subscriptions/subscribers')}
            className="mt-4 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Back to Subscribers
          </button>
        </div>
      </div>
    );
  }

  const user = data.user || data;
  const sub = data.subscription || {};
  const payments = data.payments || [];
  const history = data.history || [];
  const auditLogs = data.audit_logs || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-md hover:bg-accent transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{user.name || user.email}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <StatusBadge status={sub.status} />
      </div>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <User className="h-4 w-4" />
              User Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {user.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">{user.name || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(user.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Last Login</p>
                <p className="font-medium">{formatDate(user.last_login)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">User ID</p>
                <p className="font-mono text-xs">{userId}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Plan</p>
                <p className="font-medium">
                  <Badge variant="secondary">{sub.plan || sub.plan_slug || 'free'}</Badge>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <p><StatusBadge status={sub.status} /></p>
              </div>
              <div>
                <p className="text-muted-foreground">Current Period</p>
                <p className="font-medium text-xs">
                  {formatDate(sub.current_period_start)} - {formatDate(sub.current_period_end)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Provider</p>
                <p className="font-medium flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {sub.provider || '-'}</p>
              </div>
              {sub.cancel_at_period_end && (
                <div className="col-span-2">
                  <Badge variant="destructive">Cancels at period end</Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Admin Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => { setForceStatus('active'); setReason(''); setForceStatusModal(true); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Force Status
              <ChevronDown className="h-3 w-3" />
            </button>
            <button
              onClick={() => { setExtendDays(30); setReason(''); setExtendModal(true); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              <CalendarPlus className="h-4 w-4" />
              Extend Subscription
            </button>
            <button
              onClick={() => { setGrantPlan('pro'); setGrantDays(30); setReason(''); setGrantPlanModal(true); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              <Gift className="h-4 w-4" />
              Grant Plan
            </button>
            {sub.status === 'active' && (
              <button
                onClick={() => { setForceStatus('suspended'); setReason(''); setForceStatusModal(true); }}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
              >
                <Pause className="h-4 w-4" />
                Suspend
              </button>
            )}
            {sub.status === 'suspended' && (
              <button
                onClick={() => { setForceStatus('active'); setReason(''); setForceStatusModal(true); }}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
              >
                <Play className="h-4 w-4" />
                Reactivate
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Transaction ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No payment history
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(tx.amount, tx.currency)}</TableCell>
                    <TableCell>
                      <StatusBadge status={tx.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{tx.provider}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{tx.provider_tx_id || tx.id}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Subscription History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Subscription History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Actor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No subscription history
                  </TableCell>
                </TableRow>
              ) : (
                history.map((event, i) => (
                  <TableRow key={event.id || i}>
                    <TableCell className="text-muted-foreground">{formatDate(event.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{event.event || event.action}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{event.details || event.description || '-'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{event.actor || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Audit Log for this User */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Audit Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No audit entries
                  </TableCell>
                </TableRow>
              ) : (
                auditLogs.map((log, i) => (
                  <TableRow key={log.id || i}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                    <TableCell className="text-sm">{log.actor || log.admin_email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.action}</Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-xs truncate">{log.changes_summary || log.details || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Force Status Modal */}
      <ConfirmModal
        open={forceStatusModal}
        onClose={() => setForceStatusModal(false)}
        title="Force Subscription Status"
        description="This will immediately change the subscription status. This action is logged."
        confirmLabel={`Set to ${forceStatus}`}
        variant={forceStatus === 'suspended' || forceStatus === 'canceled' ? 'destructive' : 'default'}
        loading={actionLoading}
        onConfirm={handleForceStatus}
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium mb-1.5">New Status</label>
            <select
              value={forceStatus}
              onChange={(e) => setForceStatus(e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="past_due">Past Due</option>
              <option value="suspended">Suspended</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a reason for this action..."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>
      </ConfirmModal>

      {/* Extend Modal */}
      <ConfirmModal
        open={extendModal}
        onClose={() => setExtendModal(false)}
        title="Extend Subscription"
        description="Add additional days to the current subscription period."
        confirmLabel={`Extend by ${extendDays} days`}
        loading={actionLoading}
        onConfirm={handleExtend}
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium mb-1.5">Days to Extend</label>
            <input
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value, 10) || 0)}
              min={1}
              max={365}
              className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a reason for this extension..."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>
      </ConfirmModal>

      {/* Grant Plan Modal */}
      <ConfirmModal
        open={grantPlanModal}
        onClose={() => setGrantPlanModal(false)}
        title="Grant Plan"
        description="Grant a subscription plan to this user without requiring payment."
        confirmLabel="Grant Plan"
        loading={actionLoading}
        onConfirm={handleGrantPlan}
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium mb-1.5">Plan</label>
            <select
              value={grantPlan}
              onChange={(e) => setGrantPlan(e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {plans.length > 0 ? (
                plans.map((p) => (
                  <option key={p.slug || p.id} value={p.slug || p.id}>{p.name || p.slug}</option>
                ))
              ) : (
                <>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Duration (days)</label>
            <input
              type="number"
              value={grantDays}
              onChange={(e) => setGrantDays(parseInt(e.target.value, 10) || 0)}
              min={1}
              max={365}
              className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a reason for granting this plan..."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
}
